import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'

import type {
  InvoicingSettingsPreviewLogo,
  InvoicingSettingsPreviewPatch,
} from '../../../types/invoicingSettingsPreviewPatch'
import type { ZervantTemplateSettings } from '../../../types/templates/zervantTemplateSettings'
import { getCustomTemplateSettingsPayload } from '../../../utils/getCustomTemplateSettingsPayload'
import type { LogoAndBrandingSettingsFormValues } from '../types/logoAndBrandingSettingsFormValues'
import { getLogoAndBrandingSavedTemplateSettings } from './getLogoAndBrandingSavedTemplateSettings'
import { getLogoAndBrandingSettingsDefaultValues } from './getLogoAndBrandingSettingsDefaultValues'

const normalizeLogoId = (logoId: string | null | undefined) => logoId || ''

// The preview draws the file from content. A new id is not on the saved trade name yet,
// so the form URL has to travel with it. logoUrl alone does not count as a change.
const getLogoPreviewPatch = (logoId: string, logoUrl?: string | null): InvoicingSettingsPreviewLogo => {
  if (!logoId || !logoUrl) {
    return { id: logoId }
  }

  return { content: logoUrl, id: logoId }
}

interface GetLogoAndBrandingPreviewPatchOptions {
  documentTemplateSettings?: ZervantTemplateSettings
  savedLogoId?: string | null
  values: Partial<LogoAndBrandingSettingsFormValues>
}

export const getLogoAndBrandingPreviewPatch = (
  options: GetLogoAndBrandingPreviewPatchOptions,
): InvoicingSettingsPreviewPatch | undefined => {
  const { documentTemplateSettings, savedLogoId, values } = options
  const formValues = getLogoAndBrandingSettingsDefaultValues(values)
  const templateSettings = getCustomTemplateSettingsPayload(formValues)
  const savedTemplateSettings = getLogoAndBrandingSavedTemplateSettings(documentTemplateSettings)
  const hasFontChanged = templateSettings.font !== savedTemplateSettings.font
  const hasColorChanged = templateSettings.color.text !== savedTemplateSettings.color.text
  const hasAreasChanged = !isEqual(sortBy(templateSettings.areas), sortBy(savedTemplateSettings.areas))
  const logoId = normalizeLogoId(formValues.logoId)
  const hasLogoChanged = logoId !== normalizeLogoId(savedLogoId)
  const hasTemplateChanged = hasFontChanged || hasColorChanged || hasAreasChanged

  if (!hasTemplateChanged && !hasLogoChanged) {
    return undefined
  }

  return {
    ...(hasTemplateChanged
      ? {
          customTemplateSettings: {
            ...(hasAreasChanged ? { areas: templateSettings.areas } : {}),
            ...(hasColorChanged ? { color: templateSettings.color } : {}),
            ...(hasFontChanged ? { font: templateSettings.font } : {}),
          },
        }
      : {}),
    ...(hasLogoChanged ? { logo: getLogoPreviewPatch(logoId, formValues.logoUrl) } : {}),
  }
}
